import { ENV } from '../config/env';

// Use centralized ENV configuration for API URL
const API_BASE_URL = `${ENV.API_URL}/api`;

// Runware video model interface
export interface RunwareVideoModel {
  id: string;
  name: string;
  description: string;
  maxDuration: number;
  maxResolution: string;
  aspectRatio: string;
  inputTypes: string[];
  creditCost: number;
}

// Video generation request parameters
export interface VideoGenerationParams {
  // Core parameters
  positivePrompt: string;
  negativePrompt?: string;
  model: string;
  duration: number;
  width: number;
  height: number;
  
  // Output configuration
  outputFormat: string; // 'mp4' or 'webm'
  outputQuality: number; // Between 1-100
  
  // Optional parameters
  seed?: number;
  fps?: number;
  numberOfResults?: number;
  referenceImage?: string; // Base64 encoded image for image-to-video
  
  // Credit management
  includeCost?: boolean; // Whether to include cost calculation in response
}

// Initial video generation response
export interface VideoGenerationResponse {
  success: boolean;
  taskUUID: string;
  videoUUID?: string;
  estimatedCompletionTime?: number; // In seconds
  creditCost?: number;
  message?: string;
  error?: string;
}

// Video generation status
export interface VideoGenerationStatus {
  taskUUID: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  videoURL?: string;
  videoUUID?: string;
  error?: string;
  creditCost?: number;
  completedAt?: string;
  estimatedCompletionTime?: number; // Estimated time to completion in seconds
}

// Video result with metadata
export interface VideoResult {
  videoURL: string;
  videoUUID: string;
  width: number;
  height: number;
  duration: number;
  format: string;
  creditCost?: number;
  model: string;
  prompt: string;
  createdAt: string;
}

/**
 * Validates and corrects video generation parameters to ensure they match Runware's officially supported specifications
 * 
 * @param params The input parameters to validate
 * @returns The validated and corrected parameters
 * @throws Error if parameters cannot be corrected to meet Runware specifications
 */
const validateRunwareParams = (params: VideoGenerationParams): { 
  width: number; 
  height: number; 
  outputFormat: string;
  modelSupports1080p: boolean;
} => {
  // Validate outputFormat - Runware officially only supports MP4
  if (params.outputFormat !== 'mp4') {
    console.warn(`Unsupported output format: ${params.outputFormat}. Using MP4 instead.`);
    params.outputFormat = 'mp4';
  }
  
  // Check if the selected model supports 1080p or only 720p
  // Kling 2.1 is the only model officially supporting 1080p in our current implementation
  const modelSupports1080p = params.model.toLowerCase().includes('kling');
  
  // Determine the proper resolution based on model support
  let targetWidth = 1280;  // Default to 720p width
  let targetHeight = 720;  // Default to 720p height
  
  // If model supports 1080p and user selected 1080p format, use higher resolution
  if (modelSupports1080p && params.width >= 1920) {
    targetWidth = 1920;   // 1080p width
    targetHeight = 1080;  // 1080p height
  }
  
  // Ensure dimensions are multiples of 8 for compatibility
  const adjustedWidth = Math.ceil(targetWidth / 8) * 8;
  const adjustedHeight = Math.ceil(targetHeight / 8) * 8;
  
  return {
    width: adjustedWidth,
    height: adjustedHeight,
    outputFormat: params.outputFormat,
    modelSupports1080p
  };
};

/**
 * Initiates a video generation request to Runware API
 * 
 * @param params Video generation parameters
 * @returns Initial response with task UUID for polling
 */
export const generateVideo = async (params: VideoGenerationParams): Promise<VideoGenerationResponse> => {
  try {
    // Input validation
    if (!params.positivePrompt?.trim()) {
      throw new Error('Positive prompt is required');
    }
    
    if (!params.model) {
      throw new Error('Model selection is required');
    }
    
    if (!params.duration || params.duration <= 0) {
      throw new Error('Valid duration is required');
    }
    
    if (!params.width || !params.height || params.width <= 0 || params.height <= 0) {
      throw new Error('Valid dimensions are required');
    }
    
    if (!params.outputFormat) {
      throw new Error('Output format is required');
    }
    
    // Validate parameters against Runware's officially supported specifications
    const validatedParams = validateRunwareParams(params);
    
    // Ensure FPS is present (default to 24 if not specified)
    const fps = params.fps || 24;
    
    // Prepare request payload with a flexible interface
    interface RequestPayload {
      taskType: string;
      positivePrompt: string;
      negativePrompt: string;
      model: string;
      duration: number;
      width: number;
      height: number;
      fps: number;
      outputFormat: string;
      outputQuality: number;
      numberOfResults: number;
      includeCost: boolean;
      seed: number;
      referenceImage?: string; // Optional reference image field
    }
    
    const requestPayload: RequestPayload = {
      taskType: "videoInference",
      positivePrompt: params.positivePrompt,
      negativePrompt: params.negativePrompt || "",
      model: params.model,
      duration: params.duration,
      width: validatedParams.width,
      height: validatedParams.height,
      fps: fps,
      outputFormat: validatedParams.outputFormat,
      outputQuality: params.outputQuality,
      numberOfResults: params.numberOfResults || 1,
      includeCost: params.includeCost !== false, // Default to true
      seed: params.seed || Math.floor(Math.random() * 2147483647)
    };
    
    // Add reference image if provided (for image-to-video)
    if (params.referenceImage) {
      requestPayload.referenceImage = params.referenceImage;
    }
    
    // Make API request
    console.log('Initiating video generation with parameters:', {
      model: params.model,
      duration: params.duration,
      dimensions: `${validatedParams.width}x${validatedParams.height}`,
      outputFormat: validatedParams.outputFormat
    });
    
    const response = await fetch(`${API_BASE_URL}/videos/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
      credentials: 'include' // Include cookies for authentication
    });
    
    // Handle errors
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Video generation error:', errorText);
      
      interface ErrorResponse {
        message?: string;
      }
      
      let errorDetails: ErrorResponse = {};
      try {
        errorDetails = JSON.parse(errorText) as ErrorResponse;
      } catch {
        errorDetails = { message: errorText };
      }
      
      throw new Error(`Video generation failed: ${response.status} ${response.statusText}. ${errorDetails.message || ''}`);
    }
    
    const responseData = await response.json();
    
    return {
      success: true,
      taskUUID: responseData.taskUUID,
      videoUUID: responseData.videoUUID,
      estimatedCompletionTime: responseData.estimatedCompletionTime,
      creditCost: responseData.creditCost,
      message: responseData.message
    };
  } catch (error) {
    console.error('Error in video generation:', error);
    return {
      success: false,
      taskUUID: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Polls for the status of a video generation task
 * 
 * @param taskUUID The UUID of the task to check
 * @returns Current status of the video generation task
 */
export const checkVideoGenerationStatus = async (taskUUID: string): Promise<VideoGenerationStatus> => {
  try {
    if (!taskUUID) {
      throw new Error('Task UUID is required');
    }
    
    const response = await fetch(`${API_BASE_URL}/videos/status/${taskUUID}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include' // Include cookies for authentication
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Status check error:', errorText);
      
      throw new Error(`Status check failed: ${response.status} ${response.statusText}`);
    }
    
    const statusData = await response.json();
    
    return {
      taskUUID,
      status: statusData.status,
      progress: statusData.progress || 0,
      videoURL: statusData.videoURL,
      videoUUID: statusData.videoUUID,
      error: statusData.error,
      creditCost: statusData.creditCost,
      completedAt: statusData.completedAt
    };
  } catch (error) {
    console.error('Error checking video status:', error);
    return {
      taskUUID,
      status: 'failed',
      progress: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Fetches user's video generation history
 * 
 * @param page Page number for pagination
 * @param limit Number of results per page
 * @returns Array of video results with metadata
 */
export const getVideoHistory = async (page = 1, limit = 10): Promise<VideoResult[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/videos/history?page=${page}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include' // Include cookies for authentication
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch video history: ${response.status} ${response.statusText}`);
    }
    
    const historyData = await response.json();
    return historyData.videos || [];
  } catch (error) {
    console.error('Error fetching video history:', error);
    return [];
  }
};

/**
 * Fetches available video credits for the current user
 * 
 * @returns Number of video credits available
 */
export const getVideoCredits = async (): Promise<number> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/video-credits`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include' // Include cookies for authentication
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch video credits: ${response.status} ${response.statusText}`);
    }
    
    const creditsData = await response.json();
    return creditsData.credits || 0;
  } catch (error) {
    console.error('Error fetching video credits:', error);
    return 0;
  }
};

/**
 * Calculates the estimated credit cost for a video generation
 * 
 * @param params Video generation parameters
 * @returns Estimated credit cost
 */
export const calculateEstimatedCredits = async (params: Partial<VideoGenerationParams>): Promise<number> => {
  try {
    // If missing core parameters, return 0
    if (!params.model || !params.duration || !params.width || !params.height) {
      return 0;
    }
    
    const response = await fetch(`${API_BASE_URL}/videos/estimate-cost`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
      credentials: 'include' // Include cookies for authentication
    });
    
    if (!response.ok) {
      throw new Error(`Failed to estimate credit cost: ${response.status} ${response.statusText}`);
    }
    
    const costData = await response.json();
    return costData.creditCost || 0;
  } catch (error) {
    console.error('Error estimating credit cost:', error);
    return 0;
  }
};
